import useTranslation from "@babackapp/translations/use-translation";
import { Badge } from "@babackapp/ui/components/badge";
import { ListChecks, Package, X } from "lucide-react";
import { useAnalyticsFilters } from "../hooks/use-analytics-filters";
import { useAnalyticsFilterDefinitions } from "../hooks/use-analytics-filter-definitions";
import { AnalyticsFilterName } from "../providers/analytics-filters.provider";

export function AnalyticsFacetedFilters() {
  const t = useTranslation();
  const { shop, filters, availableCountries, productFilter, includeAllOrders, removeFilter, clearFilter, setProductFilter, setIncludeAllOrders } =
    useAnalyticsFilters();
  const filtersDefinition = useAnalyticsFilterDefinitions({
    shop,
    availableCountries,
  });

  const filtersArray = Array.from(filters.entries());

  if (!filtersArray.length && !productFilter && !includeAllOrders) return null;

  return (
    <div className="col-span-4 flex gap-2 flex-wrap">
      {includeAllOrders && (
        <div className="flex items-center gap-0.5 border rounded overflow-hidden h-6">
          <div className="flex items-center gap-1 px-1.5 bg-muted/50 h-full">
            <ListChecks className="h-3 w-3" />
          </div>
          <div className="flex items-center gap-0.5 px-1">
            <Badge
              variant="secondary"
              className="h-5 px-1.5 text-[11px] font-normal gap-0.5 cursor-pointer hover:bg-destructive/20"
              onClick={() => setIncludeAllOrders(false)}
            >
              {t("analytics.filter.includeAllOrders")}
              <X className="h-2.5 w-2.5" />
            </Badge>
          </div>
        </div>
      )}
      {productFilter && (
        <div className="flex items-center gap-0.5 border rounded overflow-hidden h-6">
          <div className="flex items-center gap-1 px-1.5 bg-muted/50 h-full">
            <Package className="h-3 w-3" />
            <span className="text-[11px] font-normal">
              {t("analytics.filter.product")}
            </span>
          </div>
          <div className="flex items-center gap-0.5 px-1">
            <Badge
              variant="secondary"
              className="h-5 px-1.5 text-[11px] font-normal gap-0.5 cursor-pointer hover:bg-destructive/20"
              onClick={() => setProductFilter(null)}
            >
              {productFilter.title}
              <X className="h-2.5 w-2.5" />
            </Badge>
          </div>
        </div>
      )}
      {filtersArray.map(([name, filter]) => {
        const filterDefinition = filtersDefinition.get(name);
        if (!filterDefinition) return null;

        return (
          <FilterBadgeGroup
            key={name}
            name={name}
            values={filter.value}
            filterDefinition={filterDefinition}
            onRemoveValue={(value) => removeFilter({ name, value })}
            onClear={() => clearFilter({ name })}
          />
        );
      })}
    </div>
  );
}

type FilterBadgeGroupProps = {
  name: AnalyticsFilterName;
  values: string[];
  filterDefinition: {
    category: { label: string; Icon?: React.ElementType };
    options: Map<string, { label: string; Icon?: React.ElementType }>;
  };
  onRemoveValue: (value: string) => void;
  onClear: () => void;
};

function FilterBadgeGroup({
  name,
  values,
  filterDefinition,
  onRemoveValue,
}: FilterBadgeGroupProps) {
  const Icon = filterDefinition.category.Icon;

  return (
    <div className="flex items-center gap-0.5 border rounded overflow-hidden h-6">
      <div className="flex items-center gap-1 px-1.5 bg-muted/50 h-full">
        {Icon && <Icon className="h-3 w-3" />}
        <span className="text-[11px] font-normal">
          {filterDefinition.category.label}
        </span>
      </div>
      <div className="flex items-center gap-0.5 px-1">
        {values.map((value) => {
          const option = filterDefinition.options.get(value);
          return (
            <Badge
              key={`${name}-${value}`}
              variant="secondary"
              className="h-5 px-1.5 text-[11px] font-normal gap-0.5 cursor-pointer hover:bg-destructive/20"
              onClick={() => onRemoveValue(value)}
            >
              {option?.label ?? value}
              <X className="h-2.5 w-2.5" />
            </Badge>
          );
        })}
      </div>
    </div>
  );
}
